import { useState, useEffect } from 'react';
import logger from '../../lib/logger';
import AdminLayout from '../../components/layout/AdminLayout';
import { pagamentosApi, participantesApi } from '../../lib/api';
import { CreditCard, Search, Eye, DollarSign, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const PagamentosPage = () => {
  const [pagamentos, setPagamentos] = useState([]);
  const [participantes, setParticipantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedPagamento, setSelectedPagamento] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pagamentosRes, participantesRes] = await Promise.all([
        pagamentosApi.list(),
        participantesApi.list(),
      ]);
      // Backend returns { total, logs, available_statuses }; participantes returns array
      const pagamentosData = pagamentosRes.data;
      setPagamentos(Array.isArray(pagamentosData) ? pagamentosData : (pagamentosData?.logs || []));
      setParticipantes(Array.isArray(participantesRes.data) ? participantesRes.data : []);
    } catch (error) {
      logger.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const openDetails = (pagamento) => {
    setSelectedPagamento(pagamento);
    setDetailsOpen(true);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "d 'de' MMM 'de' yyyy, HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getParticipanteName = (externalReference) => {
    if (!externalReference) return 'N/A';
    const participante = participantes.find(p => 
      p.participante_id === externalReference || p.id === externalReference
    );
    return participante?.nome_responsavel || externalReference;
  };

  const getParticipanteEmail = (externalReference) => {
    if (!externalReference) return '-';
    const participante = participantes.find(p => 
      p.participante_id === externalReference || p.id === externalReference
    );
    return participante?.email || '-';
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { class: 'badge-warning', label: 'Pendente', icon: Clock },
      pendente: { class: 'badge-warning', label: 'Pendente', icon: Clock },
      approved: { class: 'badge-success', label: 'Aprovado', icon: CheckCircle },
      aprovado: { class: 'badge-success', label: 'Aprovado', icon: CheckCircle },
      rejected: { class: 'badge-error', label: 'Rejeitado', icon: XCircle },
      falhou: { class: 'badge-error', label: 'Falhou', icon: XCircle },
      cancelled: { class: 'badge-error', label: 'Cancelado', icon: XCircle },
      unknown: { class: 'badge-warning', label: 'Desconhecido', icon: Clock },
    };
    const statusConfig = config[status] || config.pending;
    return (
      <span className={`${statusConfig.class} inline-flex items-center gap-1`}>
        <statusConfig.icon className="w-3 h-3" />
        {statusConfig.label}
      </span>
    );
  };

  const getMetodoBadge = (metodo) => {
    return metodo === 'cartao' 
      ? <span className="px-2 py-0.5 rounded bg-gray-400/20 text-gray-400 text-xs">Cartão</span>
      : <span className="px-2 py-0.5 rounded bg-gray-500/20 text-green-500 text-xs">PIX</span>;
  };

  const filteredPagamentos = pagamentos.filter(p => {
    const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
    const participante = participantes.find(part => 
      part.participante_id === p.external_reference || part.id === p.external_reference
    );
    const matchesSearch = searchTerm === '' || 
      participante?.nome_responsavel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      participante?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.payment_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.preference_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesStatus && matchesSearch;
  });

  // Calculate totals
  const totalAprovado = pagamentos
    .filter(p => p.status === 'approved' || p.status === 'aprovado')
    .reduce((sum, p) => sum + (p.amount || 0), 0);
  
  const totalPendente = pagamentos
    .filter(p => p.status === 'pending' || p.status === 'pendente')
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-kairos-orange border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="pagamentos-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl text-white mb-2">Pagamentos</h1>
            <p className="text-gray-400">Acompanhe todos os pagamentos</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-obsidian-200 border-white/10">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Recebido</p>
                <p className="text-2xl font-bold text-green-500">{formatCurrency(totalAprovado)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500/30" />
            </CardContent>
          </Card>
          <Card className="bg-obsidian-200 border-white/10">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Pendente</p>
                <p className="text-2xl font-bold text-yellow-500">{formatCurrency(totalPendente)}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500/30" />
            </CardContent>
          </Card>
          <Card className="bg-obsidian-200 border-white/10">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total de Transações</p>
                <p className="text-2xl font-bold text-white">{pagamentos.length}</p>
              </div>
              <CreditCard className="w-8 h-8 text-gray-600" />
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              placeholder="Buscar por nome, email ou ID da transação..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-obsidian-50 border-white/10 text-white"
              data-testid="search-pagamento-input"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full md:w-[180px] bg-obsidian-50 border-white/10 text-white">
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent className="bg-obsidian-200 border-white/10">
              <SelectItem value="all" className="text-white hover:bg-white/10">Todos</SelectItem>
              <SelectItem value="pendente" className="text-white hover:bg-white/10">Pendente</SelectItem>
              <SelectItem value="aprovado" className="text-white hover:bg-white/10">Aprovado</SelectItem>
              <SelectItem value="falhou" className="text-white hover:bg-white/10">Falhou</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {filteredPagamentos.length === 0 ? (
          <Card className="bg-obsidian-200 border-white/10">
            <CardContent className="py-16 text-center">
              <CreditCard className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Nenhum pagamento encontrado.</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-obsidian-200 border-white/10">
            <CardContent className="p-0 overflow-x-auto">
              <table className="table-dark">
                <thead>
                  <tr>
                    <th>Participante</th>
                    <th>Valor</th>
                    <th>Método</th>
                    <th>Status</th>
                    <th>ID Transação</th>
                    <th>Data</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPagamentos.map((pagamento) => (
                    <tr key={pagamento.id} data-testid={`pagamento-row-${pagamento.id}`}>
                      <td>
                        <div>
                          <p className="text-white font-medium">{getParticipanteName(pagamento.external_reference)}</p>
                          <p className="text-gray-500 text-sm">{getParticipanteEmail(pagamento.external_reference)}</p>
                        </div>
                      </td>
                      <td className="text-kairos-orange font-medium">{formatCurrency(pagamento.amount || 0)}</td>
                      <td>
                        <span className="px-2 py-0.5 rounded bg-gray-500/20 text-gray-400 text-xs">
                          {pagamento.source === 'webhook' ? 'Webhook' : 'Transação'}
                        </span>
                      </td>
                      <td>{getStatusBadge(pagamento.status)}</td>
                      <td>
                        {pagamento.payment_id ? (
                          <span className="font-mono text-gray-400 text-xs">{String(pagamento.payment_id).slice(0, 20)}...</span>
                        ) : (
                          <span className="text-gray-600">-</span>
                        )}
                      </td>
                      <td className="text-gray-400 text-sm">{formatDate(pagamento.created_at)}</td>
                      <td>
                        <div className="flex items-center justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDetails(pagamento)}
                            className="text-gray-400 hover:text-white"
                            data-testid={`view-pagamento-${pagamento.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Details Dialog */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="bg-obsidian-200 border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Detalhes do Pagamento</DialogTitle>
              <DialogDescription className="text-gray-400 text-sm">
                Informações completas da transação
              </DialogDescription>
            </DialogHeader>
            
            {selectedPagamento && (
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    selectedPagamento.status === 'aprovado' ? 'bg-gray-500/20' : 
                    selectedPagamento.status === 'pendente' ? 'bg-yellow-500/20' : 'bg-red-500/20'
                  }`}>
                    <CreditCard className={`w-8 h-8 ${
                      selectedPagamento.status === 'aprovado' ? 'text-green-500' : 
                      selectedPagamento.status === 'pendente' ? 'text-yellow-500' : 'text-red-500'
                    }`} />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-white">{formatCurrency(selectedPagamento.valor)}</p>
                    {getStatusBadge(selectedPagamento.status)}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 p-4 bg-obsidian-50 rounded-lg">
                  <div>
                    <p className="text-gray-500 text-sm">Participante</p>
                    <p className="text-white">{getParticipanteName(selectedPagamento.participante_id)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">E-mail</p>
                    <p className="text-white text-sm">{getParticipanteEmail(selectedPagamento.participante_id)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Método</p>
                    {getMetodoBadge(selectedPagamento.metodo)}
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Gateway</p>
                    <p className="text-white capitalize">{selectedPagamento.gateway}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-500 text-sm">ID da Transação</p>
                    <p className="font-mono text-kairos-orange text-sm break-all">
                      {selectedPagamento.transaction_id || '-'}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-500 text-sm">Data</p>
                    <p className="text-white">{formatDate(selectedPagamento.criado_em)}</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default PagamentosPage;
